import type { Job, Pipeline } from "@career-ops-amplify/shared";
import { getPipelineForJob } from "@/lib/pipeline-analytics";
import { isPipelineArchived } from "@/lib/pipeline-stages";

export type JobsTab = "inbox" | "pipeline" | "nogo" | "all";
export type JobSortKey = "newest" | "oldest" | "company" | "source";
export type SourceFilter =
  | ""
  | "scan"
  | "import"
  | "manual"
  | "ai-search"
  | "ats"
  | "email";

export const SOURCE_FILTER_OPTIONS: Array<{ value: SourceFilter; label: string }> = [
  { value: "", label: "All sources" },
  { value: "scan", label: "Scan" },
  { value: "import", label: "Import" },
  { value: "manual", label: "Manual" },
  { value: "ai-search", label: "AI Search" },
  { value: "ats", label: "ATS" },
  { value: "email", label: "Email" },
];

export function isNoGoJob(job: Job): boolean {
  return !job.pipelineId && (job.isArchived === true || job.status === "no_go");
}

export function isInboxJob(job: Job): boolean {
  return !job.pipelineId && !isNoGoJob(job);
}

export function isInPipelineJob(job: Job, pipelines: Pipeline[]): boolean {
  if (!job.pipelineId) return false;
  const pipeline = getPipelineForJob(job, pipelines);
  return !!pipeline && !isPipelineArchived(pipeline);
}

export function jobAddedAt(job: Job): string {
  return job.discoveredAt ?? job.createdAt;
}

export function sortJobs(jobs: Job[], sort: JobSortKey): Job[] {
  const sorted = [...jobs];
  sorted.sort((a, b) => {
    switch (sort) {
      case "oldest":
        return new Date(jobAddedAt(a)).getTime() - new Date(jobAddedAt(b)).getTime();
      case "company":
        return a.company.localeCompare(b.company);
      case "source": {
        const sa = `${a.source ?? ""}:${a.sourceDetail ?? ""}`;
        const sb = `${b.source ?? ""}:${b.sourceDetail ?? ""}`;
        return sa.localeCompare(sb);
      }
      case "newest":
      default:
        return new Date(jobAddedAt(b)).getTime() - new Date(jobAddedAt(a)).getTime();
    }
  });
  return sorted;
}

export function matchesSourceFilter(job: Job, filter: SourceFilter): boolean {
  if (!filter) return true;
  return (job.source ?? "") === filter;
}

export function sourceBadgeClass(source?: string): string {
  switch (source) {
    case "scan":
      return "bg-sky-500/10 text-sky-400";
    case "ai-search":
      return "bg-violet-500/10 text-violet-400";
    case "import":
      return "bg-amber-500/10 text-amber-600 dark:text-amber-400";
    case "manual":
      return "bg-muted/30 text-muted-foreground";
    case "ats":
      return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
    case "email":
      return "bg-rose-500/10 text-rose-600 dark:text-rose-400";
    default:
      return "bg-muted/30 text-muted-foreground";
  }
}

export function sourceLabel(source?: string): string {
  switch (source) {
    case "scan":
      return "Scan";
    case "ai-search":
      return "AI Search";
    case "import":
      return "Import";
    case "manual":
      return "Manual";
    case "ats":
      return "ATS";
    case "email":
      return "Email";
    default:
      return source ?? "Unknown";
  }
}

export function formatJobSource(job: Job): { label: string; detail?: string } {
  return {
    label: sourceLabel(job.source),
    detail: job.sourceDetail,
  };
}
